<template>
  <!-- Main Container -->
  <div class="bg-white">
    <!-- Intro Screen - Always render if showIntro is true, fixed z-index covers everything -->
    <HomeWelcomeScreen v-if="showIntro" @finished="handleIntroFinished" />

    <!-- Main Content - Hidden while intro is showing to prevent Flash of Content -->
    <div
      :class="{
        'opacity-0 h-0 overflow-hidden': showIntro,
        'opacity-100 transition-opacity duration-1000 ease-in': !showIntro,
      }"
    >
      <h1 class="sr-only">
        Tayba Travel - Votre partenaire pour Omra & Hajj en Europe
      </h1>
      <!-- Hero Slider Section -->
      <HomeHeroSlider :slides="heroSlides" />

      <!-- Packages Section -->
      <HomePackagesSection
        :packages="packages"
        :loading="pending"
        :showTabs="true"
      />

      <!-- Steps Section -->
      <HomeStepsSection />

      <!-- Épargne Section -->
      <HomeEpargneSection />

      <!-- CTA Section -->
      <UiCTASection
        title="Notre équipe dédiée est à votre écoute"
        description="Tayba travel est à votre écoute pour vous accompagner dans la préparation de votre voyage spirituel"
      />

      <!-- Floating WhatsApp Button -->
      <WhatsAppFloatingButton />
      <MessengerFloatingButton />
    </div>
  </div>
</template>

<script setup lang="ts">
import HomeHeroSlider from "~/components/home/HomeHeroSlider.vue";
import HomePackagesSection from "~/components/home/HomePackagesSection.vue";
import HomeStepsSection from "~/components/home/HomeStepsSection.vue";
import HomeEpargneSection from "~/components/home/HomeEpargneSection.vue";
import WhatsAppFloatingButton from "~/components/ui/WhatsAppFloatingButton.vue";
import MessengerFloatingButton from "~/components/ui/MessengerFloatingButton.vue";
import HomeWelcomeScreen from "~/components/home/HomeWelcomeScreen.vue";
import { gregorianToHijriYear } from "~/utils/date-utils";

// Use cookie for SSR compatibility to avoid flash of content
const introSeen = useCookie("tayba_intro_seen", {
  maxAge: 60 * 60 * 24 * 365,
  default: () => false,
});

// Start with cookie value for SSR (avoids flash in browser)
const showIntro = ref(!introSeen.value);

onMounted(() => {
  // In PWA standalone mode, sessionStorage is cleared on every app close.
  // Show the animation on each new app launch without triggering it on
  // every in-app navigation (sessionStorage survives within a session).
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    !!(window.navigator as any).standalone;

  if (isStandalone && !sessionStorage.getItem("tayba_intro_seen_session")) {
    showIntro.value = true;
  }
});

const handleIntroFinished = () => {
  showIntro.value = false;
  introSeen.value = true; // Persist for browser mode
  if (import.meta.client) {
    sessionStorage.setItem("tayba_intro_seen_session", "true"); // Persist for this PWA session
  }
};

const config = useRuntimeConfig();
const apiBase = config.public.apiBase;
const siteUrl = (
  (config.public.siteUrl as string) || "https://taybatravel.be"
).replace(/\/$/, "");

// Meta — use siteUrl (from NUXT_PUBLIC_SITE_URL) instead of useRequestURL()
// which returns http:// behind a TLS-terminating ingress.
const canonicalUrl = siteUrl;
const ogImageUrl = `${siteUrl}/images/hero-omra-2026.webp`;

useSeoMeta({
  title: "Tayba Travel - Votre partenaire pour Omra & Hajj en Europe",
  description:
    "Découvrez nos formules Omra et Hajj avec des départs réguliers, hébergements 5 étoiles à proximité des lieux saints. Épargne conforme aux principes islamiques.",
  keywords:
    "omra, hajj, pèlerinage, makkah, madinah, voyage religieux, tayba travel, omra ramadhan, épargne omra",
  ogTitle: "Tayba Travel - Votre partenaire pour Omra & Hajj en Europe",
  ogDescription:
    "Découvrez nos formules Omra et Hajj avec des départs réguliers, hébergements 5 étoiles à proximité des lieux saints. Épargne conforme aux principes islamiques.",
  ogImage: ogImageUrl,
  ogUrl: canonicalUrl,
  ogType: "website",
  twitterCard: "summary_large_image",
  twitterTitle: "Tayba Travel - Votre partenaire pour Omra & Hajj en Europe",
  twitterDescription:
    "Découvrez nos formules Omra et Hajj avec des départs réguliers, hébergements 5 étoiles à proximité des lieux saints. Épargne conforme aux principes islamiques.",
  twitterImage: ogImageUrl,
});

useHead({
  titleTemplate: "%s",
  link: [{ rel: "canonical", href: canonicalUrl }],
});

// Data fetching with useFetch (SSR-enabled)
const { data: packageData, pending } = useFetch<{ packages: any[] }>(
  `${apiBase}/public/packages`,
  {
    lazy: true,
  },
);

const packages = computed(() => {
  return packageData.value?.packages || [];
});

// Hero slides data — dynamic for packages with nextMonthlyDeparture, static fallback otherwise
const packageImageMap: Record<
  string,
  { desktop: string; mobile: string; objectPosition?: string }
> = {
  "omra-al-baraka": {
    desktop: "/images/slider-baraka-bkg.webp",
    mobile: "/images/slider-hero-al-baraka-mobile.webp",
    objectPosition: "right center",
  },
  "omra-al-khayr": {
    desktop: "/images/slider-khayr-bkg.webp",
    mobile: "/images/slider-hero-al-khayr-mobile.webp",
    objectPosition: "right center",
  },
  "omra-al-naeem": {
    desktop: "/images/slider-baraka-bkg.webp",
    mobile: "/images/slider-hero-al-baraka-mobile.webp",
    objectPosition: "right center",
  },
};

function formatDepartureBadge(dateStr: string, durationDays: number): string {
  const date = new Date(dateStr);
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();
  return `Départ ${day}/${month}/${year} · ${durationDays} Jours`;
}

const heroSlides = computed(() => {
  const dynamicSlides: any[] = [];

  // Build dynamic slides from packages with nextMonthlyDeparture, sorted by date
  const eligiblePackages = (packages.value || [])
    .filter((pkg: any) => pkg.hasDepartureTypes && pkg.nextMonthlyDeparture)
    .sort((a: any, b: any) =>
      a.nextMonthlyDeparture.date.localeCompare(b.nextMonthlyDeparture.date),
    );

  for (const pkg of eligiblePackages) {
    const images = packageImageMap[pkg.slug] || {
      desktop: "/images/slider-omra-bkg.webp",
      mobile: "/images/slider-omra-bkg-mobile.webp",
    };
    const depDate = pkg.nextMonthlyDeparture.date as string;
    dynamicSlides.push({
      id: `pkg-${pkg.slug}`,
      dateBadge: formatDepartureBadge(depDate, pkg.durationDays || 10),
      title: pkg.title,
      description:
        pkg.subtitle ||
        `Partez accomplir votre Omra dans les meilleures conditions avec la formule ${pkg.title}, un programme de ${pkg.durationDays || 10} jours conçu pour allier spiritualité, confort et sérénité.`,
      ctaText: "Commencer la réservation",
      ctaLink: `/omra/${pkg.slug}?date=${depDate}&type=MONTHLY_GROUP`,
      backgroundImage: images.desktop,
      backgroundImageMobile: images.mobile,
      price: pkg.pricing?.minPrice
        ? String(Math.round(pkg.pricing.minPrice))
        : undefined,
      overlayGradient: true,
      objectPosition: images.objectPosition,
      objectFitMobile: "cover",
      objectFit: "cover",
      objectFitDesktop: "contain",
    });
  }

  // Static slides always appended after dynamic ones
  const allPkgs = packages.value || [];

  // Derive years from actual packages
  // Ramadan: earliest departure across ramadanDepartures or fixedDepartureDate
  const ramadanPkgs = allPkgs.filter(
    (p: any) => p.isRamadan || p.category === "omra-ramadhan",
  );
  const ramadanDates: string[] = [];
  for (const p of ramadanPkgs) {
    if (Array.isArray(p.ramadanDepartures) && p.ramadanDepartures.length > 0) {
      ramadanDates.push(
        ...p.ramadanDepartures.map((d: any) => d.date as string),
      );
    } else if (p.fixedDepartureDate) {
      ramadanDates.push(String(p.fixedDepartureDate).slice(0, 10));
    }
  }
  const earliestRamadanDate = ramadanDates.sort()[0] ?? null;
  const ramadanYear = earliestRamadanDate
    ? new Date(earliestRamadanDate).getUTCFullYear()
    : new Date().getFullYear();
  const ramadanHijriYear = earliestRamadanDate
    ? gregorianToHijriYear(earliestRamadanDate)
    : gregorianToHijriYear(new Date());
  const ramadanDateBadge = earliestRamadanDate
    ? (() => {
        const d = new Date(earliestRamadanDate);
        const day = String(d.getUTCDate()).padStart(2, "0");
        const monthNames = [
          "Janvier",
          "Février",
          "Mars",
          "Avril",
          "Mai",
          "Juin",
          "Juillet",
          "Août",
          "Septembre",
          "Octobre",
          "Novembre",
          "Décembre",
        ];
        return `${day} ${monthNames[d.getUTCMonth()]} ${ramadanYear}`;
      })()
    : `Ramadhan ${ramadanYear}`;

  // Regular Omra: earliest departure year from non-Ramadan packages
  const regularPkgs = allPkgs.filter(
    (p: any) => !p.isRamadan && p.category !== "omra-ramadhan",
  );
  const regularDates: string[] = [];
  for (const p of regularPkgs) {
    if (p.nextMonthlyDeparture?.date)
      regularDates.push(p.nextMonthlyDeparture.date as string);
    else if (p.fixedDepartureDate)
      regularDates.push(String(p.fixedDepartureDate).slice(0, 10));
  }
  const earliestRegularDate = regularDates.sort()[0] ?? null;
  const omraYear = earliestRegularDate
    ? new Date(earliestRegularDate).getUTCFullYear()
    : new Date().getFullYear();

  const staticSlides = [
    {
      id: "ramadhan",
      dateBadge: `${ramadanDateBadge} / ${ramadanHijriYear} H`,
      title: `Omra Ramadhan ${ramadanYear}/${ramadanHijriYear}h`,
      description: `L'Omra Ramadhan ${ramadanYear} propose un séjour spirituel de 30 jours, incluant Madinah et Makkah avec hébergements soigneusement sélectionnés, confortables, organisés.`,
      ctaText: "Voir nos formules",
      ctaLink: "/omra-ramadhan",
      backgroundImage: "/images/slider-ramadhan-bkg.webp",
      backgroundImageMobile: "/images/slider-ramadhan-bkg-mobile.webp",
      calligraphyImage: "/images/ramadhan-logo.svg",
    },
    {
      id: `omra-${omraYear}`,
      dateBadge: "Disponible toute l'année",
      title: `Découvrez nos Omras ${omraYear}`,
      description:
        "Tayba Travel vous accompagne dans un séjour spirituel d'exception, avec des départs hebdomadaires vers les trois sites saints et des hébergements choisis avec soin pour garantir confort, sérénité et organisation optimale.",
      ctaText: "Voir nos formules",
      ctaLink: "/omra",
      backgroundImage: "/images/slider-omra-bkg.webp",
      backgroundImageMobile: "/images/slider-omra-bkg-mobile.webp",
      calligraphyImage: "/images/omra-calligraphy.svg",
    },
    {
      id: "epargne",
      dateBadge: "Épargne Tayba Travel",
      title: "Épargnez pour votre Omra & Hajj",
      description:
        "Préparez sereinement votre Omra ou votre Hajj grâce à des solutions d'épargne conformes aux principes islamiques. Des versements flexibles, accessibles et transparents pour vous accompagner pas à pas jusqu'à l'accomplissement de votre projet spirituel.",
      ctaText: "Voir nos formules",
      ctaLink: "/epargne",
      backgroundImage: "/images/slider-epargne-bkg.webp",
      backgroundImageMobile: "/images/slider-epargne-bkg-mobile.webp",
    },
  ];

  // If no dynamic slides available, prepend the generic "Découvrez" slide at top
  const allSlides =
    dynamicSlides.length > 0
      ? [...dynamicSlides, ...staticSlides]
      : staticSlides;

  // Add numeric ids for the slider component
  return allSlides.map((s, i) => ({ ...s, id: i + 1 }));
});
</script>
